import { apiService } from './api';
import type { Book, BookFormData, BookListParams, Category } from '../types';

export class BookService {
  private baseUrl = '/api/books';

  async getBooks(params: BookListParams = {}): Promise<{ books: Book[]; totalCount: number; page: number; pageSize: number }> {
    const searchParams = new URLSearchParams();
    
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    if (params.searchTerm !== undefined && params.searchTerm !== null && params.searchTerm.trim() !== '') {
      searchParams.append('searchTerm', params.searchTerm.trim());
    }
    if (params.categoryId) searchParams.append('categoryId', params.categoryId);
    if (params.availabilityFilter) searchParams.append('availabilityFilter', params.availabilityFilter);

    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
    
    const response = await apiService.get(endpoint);
    return response as { books: Book[]; totalCount: number; page: number; pageSize: number };
  }

  async getBookById(id: string): Promise<Book> {
    return apiService.get<Book>(`${this.baseUrl}/${id}`);
  }

  async createBook(bookData: BookFormData): Promise<Book> {
    return apiService.post<Book>(this.baseUrl, bookData);
  }

  async updateBook(id: string, bookData: BookFormData): Promise<Book> {
    return apiService.put<Book>(`${this.baseUrl}/${id}`, bookData);
  }

  async deleteBook(id: string): Promise<void> {
    return apiService.delete<void>(`${this.baseUrl}/${id}`);
  }

  async deleteMultipleBooks(ids: string[]): Promise<void> {
    return apiService.delete<void>(`${this.baseUrl}/bulk`, { ids });
  }

  async exportBooks(dateFrom?: Date | null, dateTo?: Date | null): Promise<Book[]> {
    const searchParams = new URLSearchParams();
    if (dateFrom) {
      searchParams.append('dateFrom', dateFrom.toISOString().split('T')[0]);
    }
    if (dateTo) {
      searchParams.append('dateTo', dateTo.toISOString().split('T')[0]);
    }
    // Get all books without pagination for export
    searchParams.append('pageSize', '10000');
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.baseUrl}/export?${queryString}` : `${this.baseUrl}/export`;
    
    const response = await apiService.get(endpoint);
    return Array.isArray(response) ? response : (response as any).books || [];
  }
}

export class CategoryService {
  private baseUrl = '/api/categories';

  async getCategories(params?: {
    page?: number;
    pageSize?: number;
    type?: 'book' | 'resource';
  }): Promise<{ categories: Category[]; totalCount: number; page: number; pageSize: number; totalPages: number }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    if (params?.type) searchParams.append('type', params.type);
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
    
    const response = await apiService.get(endpoint);
    
    // Handle both old format (array) and new format (object with pagination)
    if (Array.isArray(response)) {
      return {
        categories: response,
        totalCount: response.length,
        page: 1,
        pageSize: response.length,
        totalPages: 1
      };
    }
    
    return response as { categories: Category[]; totalCount: number; page: number; pageSize: number; totalPages: number };
  }

  async getCategoryById(id: string): Promise<Category> {
    return apiService.get<Category>(`${this.baseUrl}/${id}`);
  }

  async createCategory(categoryData: { name: string; description?: string; type?: 'book' | 'resource' }): Promise<Category> {
    return apiService.post<Category>(this.baseUrl, categoryData);
  }

  async updateCategory(id: string, categoryData: { name: string; description?: string }): Promise<Category> {
    return apiService.put<Category>(`${this.baseUrl}/${id}`, categoryData);
  }

  async deleteCategory(id: string): Promise<void> {
    return apiService.delete<void>(`${this.baseUrl}/${id}`);
  }

  async exportCategories(): Promise<Category[]> {
    const response = await apiService.get(`${this.baseUrl}/export`);
    return Array.isArray(response) ? response : [];
  }
}

export const bookService = new BookService();
export const categoryService = new CategoryService();
